/**
 * Norwegian public holidays (Norske helligdager)
 *
 * Official public holidays in Norway:
 * Fixed dates:
 * - 1. januar (Nyttårsdag) - New Year's Day
 * - 1. mai (Offentlig høytidsdag) - Labour Day
 * - 17. mai (Grunnlovsdag) - Constitution Day
 * - 25. desember (1. juledag) - Christmas Day
 * - 26. desember (2. juledag) - Boxing Day
 *
 * Easter-based (movable):
 * - Palmesøndag - Palm Sunday (Sunday before Easter)
 * - Skjærtorsdag - Maundy Thursday (Thursday before Easter)
 * - Langfredag - Good Friday (Friday before Easter)
 * - 1. påskedag - Easter Sunday
 * - 2. påskedag - Easter Monday
 * - Kristi himmelfartsdag - Ascension Day (39 days after Easter)
 * - 1. pinsedag - Whit Sunday (49 days after Easter)
 * - 2. pinsedag - Whit Monday (50 days after Easter)
 */

/**
 * Calculate Easter Sunday using the Anonymous Gregorian algorithm
 * @param {number} year - The year to calculate Easter for
 * @returns {Date} - Easter Sunday date
 */
function calculateEaster(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(year, month - 1, day);
}

/**
 * Add days to a date
 * @param {Date} date - The base date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} - New date
 */
function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Get all Norwegian public holidays for a given year
 * @param {number} year - The year to get holidays for
 * @returns {Array} - Array of holiday objects with date, name, and nameEn
 */
export function getNorwegianHolidays(year) {
    const easter = calculateEaster(year);

    const holidays = [
        // Fixed holidays
        {
            date: new Date(year, 0, 1),
            name: 'Nyttårsdag',
            nameEn: "New Year's Day",
            type: 'public',
        },
        {
            date: new Date(year, 4, 1),
            name: '1. mai',
            nameEn: 'Labour Day',
            type: 'public',
        },
        {
            date: new Date(year, 4, 17),
            name: 'Grunnlovsdag',
            nameEn: 'Constitution Day',
            type: 'public',
        },
        {
            date: new Date(year, 11, 25),
            name: '1. juledag',
            nameEn: 'Christmas Day',
            type: 'public',
        },
        {
            date: new Date(year, 11, 26),
            name: '2. juledag',
            nameEn: 'Boxing Day',
            type: 'public',
        },

        // Easter-based holidays
        {
            date: addDays(easter, -7),
            name: 'Palmesøndag',
            nameEn: 'Palm Sunday',
            type: 'public',
        },
        {
            date: addDays(easter, -3),
            name: 'Skjærtorsdag',
            nameEn: 'Maundy Thursday',
            type: 'public',
        },
        {
            date: addDays(easter, -2),
            name: 'Langfredag',
            nameEn: 'Good Friday',
            type: 'public',
        },
        {
            date: easter,
            name: '1. påskedag',
            nameEn: 'Easter Sunday',
            type: 'public',
        },
        {
            date: addDays(easter, 1),
            name: '2. påskedag',
            nameEn: 'Easter Monday',
            type: 'public',
        },
        {
            date: addDays(easter, 39),
            name: 'Kristi himmelfartsdag',
            nameEn: 'Ascension Day',
            type: 'public',
        },
        {
            date: addDays(easter, 49),
            name: '1. pinsedag',
            nameEn: 'Whit Sunday',
            type: 'public',
        },
        {
            date: addDays(easter, 50),
            name: '2. pinsedag',
            nameEn: 'Whit Monday',
            type: 'public',
        },
    ];

    // Sort by date
    holidays.sort((a, b) => a.date - b.date);

    return holidays;
}

/**
 * Get holidays for a date range (useful for calendar views)
 * @param {Date} startDate - Start of range
 * @param {Date} endDate - End of range
 * @returns {Array} - Array of holiday objects within the range
 */
export function getHolidaysInRange(startDate, endDate) {
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    let holidays = [];

    // Get holidays for all years in range
    for (let year = startYear; year <= endYear; year++) {
        holidays = holidays.concat(getNorwegianHolidays(year));
    }

    // Filter to only include holidays within the date range
    return holidays.filter(h => h.date >= startDate && h.date <= endDate);
}

/**
 * Check if a specific date is a Norwegian public holiday
 * @param {Date} date - The date to check
 * @returns {Object|null} - Holiday object if it's a holiday, null otherwise
 */
export function isNorwegianHoliday(date) {
    const year = date.getFullYear();
    const holidays = getNorwegianHolidays(year);

    return holidays.find(h =>
        h.date.getFullYear() === date.getFullYear() &&
        h.date.getMonth() === date.getMonth() &&
        h.date.getDate() === date.getDate()
    ) || null;
}

/**
 * Get holiday name for a specific date (for display in calendar)
 * @param {Date} date - The date to check
 * @returns {string|null} - Holiday name or null
 */
export function getHolidayName(date) {
    const holiday = isNorwegianHoliday(date);
    return holiday ? holiday.name : null;
}

/**
 * Convert holidays to calendar event format
 * @param {number} year - The year
 * @returns {Array} - Array of events in calendar event format
 */
export function getHolidaysAsEvents(year) {
    const holidays = getNorwegianHolidays(year);

    return holidays.map((h, idx) => ({
        id: `holiday-${year}-${idx}`,
        title: h.name,
        start: h.date,
        end: h.date,
        allDay: true,
        isHoliday: true,
        type: 'holiday',
        backgroundColor: '#dc3545',
        color: '#ffffff',
        editable: false,
    }));
}

export default {
    getNorwegianHolidays,
    getHolidaysInRange,
    isNorwegianHoliday,
    getHolidayName,
    getHolidaysAsEvents,
};
